/**
 * components/Widget/Widget.jsx
 * Главный компонент виджета.
 * Отвечает за: открытие/закрытие, RTL, роутинг экранов.
 * Не содержит бизнес-логики — делегирует хукам и экранам.
 */
import { useWidget } from '@/state/WidgetContext';
import { useLocalization } from '@/hooks/useLocalization';
import { useChat } from '@/hooks/useChat';
import { SCREENS } from '@/utils/constants';
import { liveChatService } from '@/services/liveChat';

import Header   from '@/components/Header/Header';
import NavBar   from '@/components/NavBar/NavBar';
import Footer   from '@/components/Footer/Footer';
import LiveChatResumeBar from '@/components/LiveChatResumeBar/LiveChatResumeBar';

import LanguageScreen  from '@/screens/LanguageScreen';
import WelcomeScreen   from '@/screens/WelcomeScreen';
import IndustryScreen  from '@/screens/IndustryScreen';
import GoalScreen      from '@/screens/GoalScreen';
import RegionScreen    from '@/screens/RegionScreen';
import FormatScreen    from '@/screens/FormatScreen';
import EventsScreen      from '@/screens/EventsScreen';
import EventDetailScreen from '@/screens/EventDetailScreen';
import CatchupScreen     from '@/screens/CatchupScreen';
import NameScreen      from '@/screens/NameScreen';
import CompanyScreen   from '@/screens/CompanyScreen';
import ContactsScreen  from '@/screens/ContactsScreen';
import ChannelScreen   from '@/screens/ChannelScreen';
// import TelegramScreen  from '@/screens/TelegramScreen';
import EmailConfirmScreen from '@/screens/EmailConfirmScreen';
import EmailEditScreen    from '@/screens/EmailEditScreen';
import EmailInquiryScreen from '@/screens/EmailInquiryScreen';
import ManagerScreen   from '@/screens/ManagerScreen';
import LiveChatScreen  from '@/screens/LiveChatScreen';
import OfflineScreen   from '@/screens/OfflineScreen';
import LeadErrorScreen from '@/screens/LeadErrorScreen';
import GuideScreen     from '@/screens/GuideScreen';
import { FaqScreen, FaqUnsureScreen } from '@/screens/FaqScreen';

import widgetStyles from '@/styles/widget.module.scss';
import chatStyles   from '@/styles/chat.module.scss';
import { useEffect, useState } from 'react';

/** Роутер экранов — возвращает нужный компонент */
function ScreenRouter({ screen }) {
  switch (screen) {
    case SCREENS.LANGUAGE:   return <LanguageScreen />;
    case SCREENS.WELCOME:    return <WelcomeScreen />;
    case SCREENS.SCENARIO:   return <WelcomeScreen />;  // scenario = welcome с другим контекстом
    case SCREENS.INDUSTRY:   return <IndustryScreen />;
    case SCREENS.GOAL:       return <GoalScreen />;
    case SCREENS.GEOGRAPHY:  return <RegionScreen />;
    case SCREENS.FORMAT:     return <FormatScreen />;
    case SCREENS.RESULTS:      return <EventsScreen />;
    case SCREENS.EVENT_DETAIL: return <EventDetailScreen />;
    case SCREENS.CATCHUP:      return <CatchupScreen />;
    case SCREENS.NAME:       return <NameScreen />;
    case SCREENS.COMPANY:    return <CompanyScreen />;
    case SCREENS.CONTACTS:   return <ContactsScreen />;
    case SCREENS.CHANNEL:    return <ChannelScreen />;
    // case SCREENS.TELEGRAM:   return <TelegramScreen />;
    case SCREENS.EMAIL_CONFIRM: return <EmailConfirmScreen />;
    case SCREENS.EMAIL_EDIT:    return <EmailEditScreen />;
    case SCREENS.EMAIL_INQUIRY: return <EmailInquiryScreen />;
    case SCREENS.MANAGER:    return <ManagerScreen />;
    case SCREENS.LIVE_CHAT:  return <LiveChatScreen />;
    case SCREENS.OFFLINE:    return <OfflineScreen />;
    case SCREENS.LEAD_ERROR: return <LeadErrorScreen />;
    case SCREENS.GUIDE:      return <GuideScreen />;
    case SCREENS.FAQ:        return <FaqScreen />;
    case SCREENS.FAQ_UNSURE: return <FaqUnsureScreen />;
    default:                 return <WelcomeScreen />;
  }
}

/** Иконки триггера */
const ChatIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" /></svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
);

export default function Widget() {
  const { state, toggleOpen, setOpen } = useWidget();
  const { dir } = useLocalization();
  const { sendFreeText } = useChat();
  const { isOpen, screen } = state;
  const [needsResume, setNeedsResume] = useState(() => liveChatService.needsResume());
  const [isChatConnected, setIsChatConnected] = useState(() => liveChatService.isConnected());
  const isLiveChatScreen = screen === SCREENS.LIVE_CHAT || screen === SCREENS.MANAGER;

  useEffect(() => {
    return liveChatService.subscribeNeedsResume(setNeedsResume);
  }, []);

  useEffect(() => {
    return liveChatService.subscribeConnection(setIsChatConnected);
  }, []);

  // Сообщаем родительской странице (iframe) об изменении состояния и направления.
  // При закрытии задерживаем сообщение на 300мс — ждём окончания CSS-анимации,
  // чтобы iframe не сжимался раньше, чем виджет успеет анимироваться.
  useEffect(() => {
    const delay = isOpen ? 0 : 300;
    const timer = setTimeout(() => {
      window.parent.postMessage({ type: 'ibc-widget-resize', isOpen, dir }, '*');
    }, delay);
    return () => clearTimeout(timer);
  }, [isOpen, dir]);

  // Header/NavBar скрываем на экране выбора языка; Footer — только в live chat
  const showChrome = screen !== SCREENS.LANGUAGE;
  const showFooter = isLiveChatScreen && isChatConnected;

  return (
    <>
      {/* Плавающая кнопка-триггер */}
      <div
        className={[
          widgetStyles.triggerWrap,
          !isOpen ? widgetStyles.pulsing : '',
        ].join(' ')}
        dir={dir}
      >
        <button
          className={widgetStyles.trigger}
          onClick={toggleOpen}
          aria-label="Open chat"
          type="button"
        >
          {isOpen ? <CloseIcon /> : <ChatIcon />}
        </button>
      </div>

      {/* Основной виджет */}
      <div
        className={[widgetStyles.widget, isOpen ? widgetStyles.open : ''].join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label="IBC Assistant"
        dir={dir}
      >
        {showChrome && (
          <>
            <Header
              onMinimize={() => setOpen(false)}
              onClose={() => setOpen(false)}
            />
            <NavBar />
          </>
        )}

        {screen === SCREENS.LIVE_CHAT || screen === SCREENS.MANAGER ? (
          <ScreenRouter screen={screen} />
        ) : (
          <div className={chatStyles.messages}>
            <ScreenRouter screen={screen} />
          </div>
        )}

        {showChrome && isLiveChatScreen && needsResume && (
          <LiveChatResumeBar />
        )}
        {showChrome && showFooter && !needsResume && (
          <Footer onSend={sendFreeText} />
        )}
      </div>
    </>
  );
}
